import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useAuth } from "hooks/useAuth";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import ConfirmDialog from "components/common/ConfirmDialog";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { db } from "services/firebase";
import { getLanguageName } from "shared/services/constants/languages";
import {
  subscribeInterpreterRequestsForFacilities,
  updateInterpreterRequest,
} from "services/interpreterRequests/interpreterRequestService";

function minutesWaiting(requestedAt) {
  const ms = requestedAt?.toMillis?.() || 0;
  if (!ms) return 0;
  return Math.max(0, Math.floor((Date.now() - ms) / 60000));
}

export default function InterpreterRequestsPage() {
  const { user } = useAuth();
  const [facilityIds, setFacilityIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [facilitiesError, setFacilitiesError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [success, setSuccess] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setFacilityIds([]);
      return undefined;
    }
    setFacilitiesError("");
    const unsub = db
      .collection("locations")
      .where("users", "array-contains", user.uid)
      .onSnapshot(
        (snap) => {
          setFacilityIds(snap.docs.map((d) => d.id));
        },
        (err) => {
          setFacilitiesError(err?.message || "Unable to load facilities.");
        },
      );
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    setLoadError("");
    return subscribeInterpreterRequestsForFacilities(
      facilityIds,
      setRows,
      (err) => setLoadError(err?.message || "Unable to load interpreter requests."),
    );
  }, [facilityIds]);

  const title = useMemo(() => {
    const n = rows.length;
    return n > 0 ? `(${n}) Interpreter requests` : "Interpreter requests";
  }, [rows.length]);

  const runAction = async (requestId, action) => {
    setBusyId(requestId);
    setSuccess("");
    try {
      await updateInterpreterRequest({ requestId, action });
      setSuccess(
        action === "acknowledge"
          ? "Request acknowledged."
          : action === "fulfill"
            ? "Request marked fulfilled."
            : "Request cancelled.",
      );
    } catch (err) {
      setLoadError(err?.message || "Update failed.");
    } finally {
      setBusyId("");
      setCancelTarget(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="interpreter-requests-page">
      <DashboardPageHeader title={title} />

      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}
      {facilitiesError || loadError ? (
        <DataLoadErrorAlert
          message={facilitiesError || loadError}
          sx={{ mb: 2 }}
        />
      ) : null}

      {rows.length === 0 && !loadError && !facilitiesError ? (
        <Typography color="text.secondary">
          No open interpreter requests for your facilities.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {rows.map((row) => (
            <Box
              key={row.id}
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
              data-testid="interpreter-request-row"
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {getLanguageName(row.patientLanguage) || row.patientLanguage}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.roomLabel ? `Room: ${row.roomLabel} · ` : null}
                    Waiting {minutesWaiting(row.requestedAt)} min
                  </Typography>
                  <Chip
                    size="small"
                    label={row.status}
                    sx={{ mt: 1 }}
                    color={row.status === "open" ? "warning" : "info"}
                  />
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {row.status === "open" ? (
                    <LoadingButton
                      variant="contained"
                      loading={busyId === row.id}
                      onClick={() => void runAction(row.id, "acknowledge")}
                    >
                      Acknowledge
                    </LoadingButton>
                  ) : null}
                  {row.status === "acknowledged" ? (
                    <LoadingButton
                      variant="contained"
                      loading={busyId === row.id}
                      onClick={() => void runAction(row.id, "fulfill")}
                    >
                      Fulfilled
                    </LoadingButton>
                  ) : null}
                  <LoadingButton
                    color="inherit"
                    loading={busyId === row.id}
                    onClick={() => setCancelTarget(row)}
                  >
                    Cancel
                  </LoadingButton>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title="Cancel interpreter request?"
        message="Staff will no longer see this request as open."
        confirmLabel="Cancel request"
        confirmColor="error"
        loading={busyId === cancelTarget?.id}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => void runAction(cancelTarget.id, "cancel")}
      />
    </Box>
  );
}
